import React, {
  useEffect,
  useState,
  memo,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Checkbox,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  Typography,
  Stack,
  Chip,
} from "@mui/material";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import { Input, InputGroup, Pagination } from "rsuite";
import { formatAmount } from "../../../../../HelperFunctions/HelperFunctions";
import { useRouteLoaderData } from "react-router-dom";

const BatchAmountTable = ({
  data,
  setBatchAmount,
  batchCard,
  setLinkBatch,
  linkBatch,
  page: externalPage,
  setPage: setExternalPage,
  tab,
  section,
  saveScrollPosition,
  getScrollPosition,
}) => {
  const { rowsPerPage } = useRouteLoaderData("reconciliation");
  const [page, setPage] = useState(externalPage ?? 0);
  const [selectedRows, setSelectedRows] = useState([]);
  const tableContainerRef = useRef(null);
  const scrollRestoredRef = useRef(false);


  // console.log("batchhhhhhhhhhhh dataaaaaaaaaaaaaa

  // Update external page state when internal page changes
  const handlePageChange = useCallback(
    (newPage) => {
      setPage(newPage);
      if (setExternalPage) {
        setExternalPage(newPage);
      }
    },
    [setExternalPage],
  );



  // Sync internal page state with external page prop
  useEffect(() => {
    if (externalPage !== undefined && externalPage !== page) {
      setPage(externalPage);
    }
  }, [externalPage, page]);

  // useEffect(() => {
  //     setSelectedRows([]);
  //     setPage(0);
  //     setLinkBatch(prev => ({
  //         ...prev,
  //         merchant_ids: [],
  //     }));
  // }, [batchCard]);
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(data.length / rowsPerPage) - 1);
    const currentPage = externalPage !== undefined ? externalPage : page;
    const newPage = currentPage > maxPage ? maxPage : currentPage;
    if (newPage !== currentPage) {
      handlePageChange(newPage);
    }
  }, [data.length, rowsPerPage, externalPage, page, handlePageChange]);

  useEffect(() => {
    setSelectedRows([]);
    setLinkBatch((prev) => ({
      ...prev,
      merchant_ids: [],
    }));
  }, [batchCard]);

  // Clear selections when linkBatch is cleared
  useEffect(() => {
    if (!linkBatch?.merchant_ids || linkBatch.merchant_ids.length === 0) {
      setSelectedRows([]);
    }
  }, [linkBatch?.merchant_ids]);

  // Restore selections from linkBatch when component mounts or data changes
  useEffect(() => {
    if (linkBatch?.merchant_ids?.length > 0 && data?.length > 0) {
      const restoredRows = data.filter((row) =>
        linkBatch.merchant_ids.includes(row._id),
      );
      // Only restore if we have rows to restore and current selection is empty or different
      if (restoredRows.length > 0) {
        setSelectedRows((prev) => {
          const prevIds = prev.map((r) => r._id).sort();
          const restoredIds = restoredRows.map((r) => r._id).sort();
          // Only update if different
          if (JSON.stringify(prevIds) !== JSON.stringify(restoredIds)) {
            return restoredRows;
          }
          return prev;
        });
      }
    }
  }, [data, linkBatch?.merchant_ids]);

  useEffect(() => {
    const total = selectedRows.reduce(
      (sum, row) => sum + Number(row.net_amount || 0),
      0,
    );
    setBatchAmount(total.toFixed(2));
  }, [selectedRows]);

  // Restore scroll position when component mounts or section is expanded
  useEffect(() => {
    if (
      tableContainerRef.current &&
      tab &&
      section &&
      getScrollPosition &&
      !scrollRestoredRef.current &&
      data.length > 0
    ) {
      const savedScrollTop = getScrollPosition(tab, section, "batch");
      if (savedScrollTop > 0) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop = savedScrollTop;
            scrollRestoredRef.current = true;
          }
        }, 150);
      } else {
        scrollRestoredRef.current = true;
      }
    }
  }, [tab, section, getScrollPosition, data.length]);

  // Save scroll position on scroll
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || !tab || !section || !saveScrollPosition) return;

    const handleScroll = () => {
      saveScrollPosition(tab, section, "batch", container.scrollTop);
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [tab, section, saveScrollPosition]);

  // Reset scroll restored flag when data changes significantly
  useEffect(() => {
    scrollRestoredRef.current = false;
  }, [data.length]);

  const columns = [
    { name: "Type", dataKey: "card_type", width: 50 },
    { name: "Date", dataKey: "settlement_date", width: 50 },
    { name: "Deposit", dataKey: "gross_amount", width: 50 },
    { name: "Adjustments", dataKey: "financial_adjustments", width: 50 },
    { name: 'Settlement Fees', dataKey: 'settlement_fees', width: 50 },
    { name: "Net Deposit", dataKey: "net_amount", width: 50 },
  ];

  const total = data.length;
  const totalPages = Math.ceil(total / rowsPerPage);
  const grandTotalAmount = data.reduce(
    (sum, row) => sum + Number(row.net_amount),
    0,
  );

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return data.slice(start, start + rowsPerPage);
  }, [data, page, rowsPerPage]);
  const pageTotalAmount = paginatedData.reduce(
    (sum, row) => sum + Number(row.net_amount),
    0,
  );

  const handleSelectAll = (event, currentPageRows) => {
    if (event.target.checked) {
      const newSelections = currentPageRows.filter(
        (row) => !selectedRows.some((sel) => sel._id === row._id),
      );
      setSelectedRows((prev) => [...prev, ...newSelections]);
      setLinkBatch((prev) => ({
        ...prev,
        merchant_ids: [
          ...prev.merchant_ids,
          ...newSelections
            .map((r) => r._id)
            .filter((id) => !prev.merchant_ids.includes(id)),
        ],
      }));
    } else {
      const idsToRemove = currentPageRows.map((r) => r._id);
      setSelectedRows((prev) =>
        prev.filter((row) => !idsToRemove.includes(row._id)),
      );
      setLinkBatch((prev) => ({
        ...prev,
        merchant_ids: prev.merchant_ids.filter(
          (id) => !idsToRemove.includes(id),
        ),
      }));
    }
  };

  // const handleSelectRow = (row) => {
  //     setSelectedRows(prev =>
  //         prev.some(r => r._id === row._id)
  //             ? prev.filter(r => r._id !== row._id)
  //             : [...prev, row]
  //     );
  // };

  const handleSelectRow = (row) => {
    setSelectedRows((prev) => {
      const exists = prev.some((r) => r._id === row._id);

      if (exists) {
        setLinkBatch((lb) => ({
          ...lb,
          merchant_ids: lb.merchant_ids.filter((id) => id !== row._id),
        }));
        return prev.filter((r) => r._id !== row._id);
      } else {
        setLinkBatch((lb) => ({
          ...lb,
          merchant_ids: [...lb.merchant_ids, row._id],
        }));
        return [...prev, row];
      }
    });
  };

  const isSelected = (id) => selectedRows.some((row) => row._id === id);

  const ValueCell = memo(({ value }) => {
    const displayValue =
      typeof value === "object" && value !== null
        ? value.day || "-"
        : value || "-";
    return <span title={displayValue}>{formatAmount(displayValue)}</span>;
  });

  return (
    <>
      <TableContainer
        ref={tableContainerRef}
        sx={{ height: 300, overflow: "auto", mt: 2 }}
      >
        <Table stickyHeader sx={{ width: "100%" }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#d7d7d7" }}>
              <TableCell
                padding="checkbox"
                sx={{
                  backgroundColor: "#d7d7d7",
                  borderRight: "1px solid white",
                }}
              >
                <Checkbox
                  size="small"
                  color="default"
                  checked={
                    paginatedData.length > 0 &&
                    paginatedData.every((row) => isSelected(row._id))
                  }
                  indeterminate={
                    paginatedData.some((row) => isSelected(row._id)) &&
                    !paginatedData.every((row) => isSelected(row._id))
                  }
                  onChange={(e) => handleSelectAll(e, paginatedData)}
                />
              </TableCell>
              {columns.map((col, idx) => (
                <TableCell
                  key={idx}
                  sx={{
                    backgroundColor: "#d7d7d7",
                    borderRight: "1px solid white",
                    fontSize: 13,
                    maxWidth: col.width,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <span title={col.name}>{col.name}</span>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 4}
                  align="center"
                  sx={{ fontSize: 14, color: "#999" }}
                >
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell
                    padding="checkbox"
                    sx={{ borderRight: "1px solid #f2f2f2" }}
                  >
                    <Checkbox
                      size="small"
                      color="default"
                      checked={isSelected(row._id)}
                      onChange={() => handleSelectRow(row)}
                    />
                  </TableCell>
                  {columns.map((col, idx) => (
                    <TableCell
                      key={idx}
                      sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#626262",
                        borderRight: "1px solid #f2f2f2",
                        maxWidth: col.width,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textAlign:
                          col.name === "Deposit" || col.name === "Net Deposit"
                            ? "end"
                            : null,
                      }}
                    >
                      {col.name === "Chargebacks" ? (
                        <>
                          <Chip
                            sx={{
                              width: "100%",
                              borderRadius: 1,
                              backgroundColor: "#f8ced3",
                              fontSize: "11px",
                              color: "#D9001B",
                              // px: 2,
                              height: "17px",
                              "& .MuiChip-icon": {
                                color: "#D9001B",
                              },
                            }}
                            label={
                              <span title={row[col.dataKey] ?? "-"}>
                                {row[col.dataKey] !== undefined &&
                                  row[col.dataKey] !== null
                                  ? row[col.dataKey]
                                  : "-"}
                              </span>
                            }
                            size="small"
                          />
                        </>
                      ) : (
                        <ValueCell value={row[col.dataKey]} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent={"space-between"}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            p: 1,
            gap: 1,
          }}
        >
          <Typography fontSize={"12px"}>Page</Typography>

          <button
            onClick={() => {
              if (page > 0) {
                handlePageChange(page - 1);
                setTimeout(() => {
                  if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = 0;
                  }
                }, 0);
              }
            }}
            disabled={page === 0}
            style={{
              fontSize: "10px",
              cursor: page === 0 ? "not-allowed" : "pointer",

              backgroundColor: page >= totalPages - 1 ? "#f5f5f5" : "white",
            }}
          >
            <KeyboardArrowLeftOutlinedIcon fontSize="small" />
          </button>

          <input
            type="text"
            defaultValue={page + 1}
            key={page}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const value = e.target.value.trim();
                let newPage;
                if (value === "" || value === "0") {
                  newPage = 0;
                } else {
                  const parsedPage = parseInt(value);
                  if (
                    !isNaN(parsedPage) &&
                    parsedPage >= 1 &&
                    parsedPage <= totalPages
                  ) {
                    newPage = parsedPage - 1;
                  } else if (parsedPage > totalPages) {
                    newPage = totalPages - 1;
                  } else {
                    newPage = 0;
                  }
                }
                handlePageChange(newPage);
                setTimeout(() => {
                  if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = 0;
                  }
                }, 0);
                e.target.blur();
              }
            }}
            onFocus={(e) => {
              e.target.select();
            }}
            style={{
              width: "50px",
              padding: "4px",
              fontSize: "12px",
              textAlign: "center",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />

          <button
            onClick={() => {
              if (page < totalPages - 1) {
                handlePageChange(page + 1);
                setTimeout(() => {
                  if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = 0;
                  }
                }, 0);
              }
            }}
            disabled={page >= totalPages - 1}
            style={{
              // padding: '4px 8px',
              fontSize: "10px",
              cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",

              backgroundColor: page >= totalPages - 1 ? "#f5f5f5" : "white",
            }}
          >
            <KeyboardArrowRightOutlinedIcon fontSize="small" />
          </button>

          <span style={{ fontSize: "14px" }}>of {totalPages}</span>
        </Box>

        <Stack direction={"row"} gap={1}>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography fontSize={"11px"}>Page Total</Typography>

            <input
              // value={pageTotalAmount}
              value={formatAmount(pageTotalAmount)}
              disabled
              readOnly
              style={{
                fontSize: "12px",
                padding: "2px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                textAlign: "right",
              }}
            />
          </Stack>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography fontSize={"11px"}>Grand Total</Typography>

            <input
              value={formatAmount(grandTotalAmount)}
              disabled
              readOnly
              style={{
                fontSize: "12px",
                padding: "2px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                textAlign: "right",
              }}
            />
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};

export default BatchAmountTable;
